import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useSearchParams, Link } from 'react-router-dom';
import { 
  CheckCircle,
  ArrowRight,
  Shield,
  Users,
  Mail,
  Key,
  AlertCircle,
  Calendar
} from 'lucide-react';
import toast from 'react-hot-toast';

const SupportPaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pollCount, setPollCount] = useState(0);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus(sessionId);
    } else {
      setLoading(false);
    }
  }, [sessionId]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000; // 2 seconds

    if (attempts >= maxAttempts) {
      setPaymentStatus({ error: 'Payment verification timed out' });
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/support/v1/checkout/status/${sessionId}`);
      
      if (!response.ok) {
        throw new Error('Failed to check payment status');
      }

      const data = await response.json();
      
      if (data.payment_status === 'paid') {
        setPaymentStatus({ ...data, success: true });
        setLoading(false);
        toast.success('Payment successful! Your support plan is now active.');
        return;
      } else if (data.status === 'expired') {
        setPaymentStatus({ error: 'Payment session expired' });
        setLoading(false);
        return;
      }

      // If payment is still pending, continue polling
      setPollCount(attempts + 1);
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
      
    } catch (error) {
      console.error('Error checking payment status:', error);
      setPaymentStatus({ error: 'Error verifying payment status' });
      setLoading(false);
    }
  };

  const formatPrice = (cents, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
      minimumFractionDigits: 0
    }).format(cents / 100);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-azellar-light via-blue-50 to-cyan-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="animate-spin w-16 h-16 border-4 border-azellar-teal border-t-transparent rounded-full mx-auto mb-6"></div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            Processing Your Support Plan...
          </h2>
          <p className="text-gray-600 dark:text-gray-300 mb-2">
            Please wait while we activate your support plan and create your account.
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Attempt {pollCount + 1} of 5
          </p>
        </div>
      </div>
    );
  }

  if (!sessionId || (paymentStatus && paymentStatus.error)) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-azellar-light via-blue-50 to-cyan-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="w-16 h-16 bg-red-100 dark:bg-red-900 rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="w-8 h-8 text-red-600 dark:text-red-400" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            Payment Verification Failed
          </h2>
          <p className="text-gray-600 dark:text-gray-300 mb-6">
            {paymentStatus?.error || 'Unable to verify payment status. Please contact support if you were charged.'}
          </p>
          <div className="space-y-4">
            <Link to="/contact" className="btn-primary w-full">
              Contact Support
            </Link>
            <Link to="/support/plans" className="btn-outline w-full">
              Back to Support Plans
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-azellar-light via-blue-50 to-cyan-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 pt-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center"
        >
          {/* Success Icon */}
          <div className="w-24 h-24 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center mx-auto mb-8">
            <CheckCircle className="w-12 h-12 text-green-600 dark:text-green-400" />
          </div>

          {/* Success Message */}
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
            Support Plan Activated!
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-300 mb-8">
            Your support plan is now active and your client account has been created.
          </p>

          {/* Payment Details Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8 mb-8 max-w-2xl mx-auto"
          >
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              Payment Summary
            </h3>
            
            <div className="space-y-4">
              <div className="flex justify-between items-center py-3 border-b border-gray-200 dark:border-gray-600">
                <span className="text-gray-600 dark:text-gray-400">Amount Paid</span>
                <span className="text-2xl font-bold gradient-text">
                  {formatPrice(paymentStatus.amount_total, paymentStatus.currency)}
                </span>
              </div>
              
              <div className="flex justify-between items-center py-3 border-b border-gray-200 dark:border-gray-600">
                <span className="text-gray-600 dark:text-gray-400">Support Plan</span>
                <span className="font-semibold text-gray-900 dark:text-white">
                  {paymentStatus.metadata?.plan_name || 'Support Plan'}
                </span>
              </div>
              
              <div className="flex justify-between items-center py-3 border-b border-gray-200 dark:border-gray-600">
                <span className="text-gray-600 dark:text-gray-400">Plan Type</span>
                <span className="font-semibold text-gray-900 dark:text-white capitalize">
                  {paymentStatus.metadata?.plan_id || 'Monthly'} Plan
                </span>
              </div>
              
              <div className="flex justify-between items-center py-3">
                <span className="text-gray-600 dark:text-gray-400">Status</span>
                <span className="px-3 py-1 bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 rounded-full text-sm font-medium">
                  Active
                </span>
              </div>
            </div>
          </motion.div>

          {/* Account Creation Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8 mb-8 max-w-2xl mx-auto"
          >
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              Your Client Account
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="text-center p-4">
                <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                </div>
                <h4 className="font-semibold text-gray-900 dark:text-white mb-2">
                  Check Your Email
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Login credentials have been sent to your email address.
                </p>
              </div>
              
              <div className="text-center p-4">
                <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Key className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                </div>
                <h4 className="font-semibold text-gray-900 dark:text-white mb-2">
                  Secure Access
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Please change your password after your first login.
                </p>
              </div>
              
              <div className="text-center p-4">
                <div className="w-12 h-12 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="w-6 h-6 text-green-600 dark:text-green-400" />
                </div>
                <h4 className="font-semibold text-gray-900 dark:text-white mb-2">
                  Support Portal
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Access your dedicated support portal and submit tickets.
                </p>
              </div>
              
              <div className="text-center p-4">
                <div className="w-12 h-12 bg-yellow-100 dark:bg-yellow-900 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />
                </div>
                <h4 className="font-semibold text-gray-900 dark:text-white mb-2">
                  Team Access
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Add up to 5 team members to your support plan.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Action Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6"
          >
            <Link 
              to="/auth/login"
              className="btn-primary text-lg px-8 py-4 shadow-glow"
            >
              <Key className="w-5 h-5 mr-2" />
              Login to Support Portal
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
            
            <Link 
              to="/support/plans"
              className="btn-outline text-lg px-8 py-4"
            >
              View Plan Details
            </Link>
          </motion.div>

          {/* Support Note */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            className="mt-12 p-6 bg-gray-50 dark:bg-gray-800 rounded-xl max-w-2xl mx-auto"
          >
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              Need help getting started? Our support team is ready to assist you.{' '}
              <Link to="/contact" className="text-azellar-teal hover:text-azellar-navy font-medium">
                Contact us
              </Link>
              {' '}anytime.
            </p>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default SupportPaymentSuccess;